import { Component, computed, effect, inject, input, linkedSignal, model } from '@angular/core';
import { FormBuilder, FormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideEye, lucideTrash } from '@ng-icons/lucide';
import { HlmAutocompleteImports, provideHlmAutocompleteConfig } from '@spartan-ng/helm/autocomplete';
import { HlmFieldImports } from '@spartan-ng/helm/field';

import { Cartographie } from '../../../../cartographie/models/cartographie.model';
import { InterrelationStore } from '../../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Aptitude } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';
import { EtapeStore } from '../../../parcours-etape/store/etape.store';
import { ModuleStore } from '../../../parcours-module/store/module.store';
import { ModuleDetailFormService } from '../module-detail-form.service';
import { ModuleObjectifGroupComponent } from './objectif-group/objectif-group.component';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-detail-objectifs',
  imports: [HlmFieldImports, FormsModule, HlmAutocompleteImports, ModuleObjectifGroupComponent],
  templateUrl: './detail-objectifs.component.html',
  providers: [
    provideIcons({
      lucideTrash,
      lucideEye,
    }),
    provideHlmAutocompleteConfig({
      transformOptionToString: (option: Aptitude) => option.nom,
      transformValueToSearch: (option: Aptitude) => option.nom,
    }),
  ],
})
export class DetailObjectifsComponent {
  readonly #moduleStore = inject(ModuleStore);
  readonly #etapeStore = inject(EtapeStore);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #fb = inject(FormBuilder);

  readonly detailFormService = inject(ModuleDetailFormService);
  readonly form = this.detailFormService.form;

  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly peutModifier = input.required<boolean>();

  moduleSelectionne = computed(() => this.#moduleStore.selectedEntity());
  etapeSelectionnee = computed(() => this.#etapeStore.selectedEntity());
  readonly aptitudesDisponibles = linkedSignal(() => this.detailFormService.aptitudesDisponibles());
  readonly aptitudesFiltrees = linkedSignal(() => this.aptitudesDisponibles());

  nouvelleAptitude = model<Aptitude | null>();

  constructor() {
    effect(() => {
      this.detailFormService.referentiel.set(this.referentiel());
    });

    effect(() => {
      const nouvelleAptitude = this.nouvelleAptitude();
      const module = this.moduleSelectionne();
      if (nouvelleAptitude && module) {
        const objectifForm = this.#fb.group({
          aptitude: [nouvelleAptitude],
          interrelations: [[...this.getInterrelationsFromAptitude(nouvelleAptitude)]],
          productions: [[...this.getProductionsFromAptitude(nouvelleAptitude)]],
          estEvaluee: [false],
          objectif: [],
        });
        this.objectifs.push(objectifForm);
        this.nouvelleAptitude.set(null);
      }
    });
  }

  getInterrelationsFromAptitude(aptitude: Aptitude) {
    const etape = this.etapeSelectionnee();
    let interrelations: { id: string }[] = [];
    if (etape) {
      interrelations = this.#interrelationStore
        .entities()
        .filter(
          (x) => x.idsPhase?.includes(aptitude.idPhase) && etape.interrelations.some((y) => y.idInterrelation === x.id),
        )
        .map((x) => ({ id: `${aptitude.id}-${x.id}` }));
    }
    return interrelations;
  }

  getProductionsFromAptitude(aptitude: Aptitude) {
    const etape = this.etapeSelectionnee();
    let productions: Production[] = aptitude.productions ?? [];
    if (etape && etape.productions) {
      productions = aptitude.productions?.filter((x) => etape.productions?.some((y) => y.id === x.id)) ?? [];
    }
    return productions;
  }

  filterAptitudes(search: string) {
    const aptitudesDisponibles = this.aptitudesDisponibles();
    if (aptitudesDisponibles) {
      if (!search) this.aptitudesFiltrees.set(aptitudesDisponibles);

      this.aptitudesFiltrees.set(
        aptitudesDisponibles.filter((x) => x.nom.toLowerCase().includes(search.toLowerCase())),
      );
    }
  }

  supprimerAptitude(index: number) {
    this.objectifs.removeAt(index);
  }

  get objectifs() {
    return this.detailFormService.objectifs;
  }
}
